import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  History as HistoryIcon,
  LayoutDashboard,
  ListTodo,
  Loader2,
  MessageSquare,
  MessagesSquare,
  PaintbrushVertical,
  Search,
  Share2,
  Star,
  Tag,
  Telescope,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/shared";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { useToast } from "@/components/shared/Toast";
import { featureFlags } from "@/config/featureFlags";
import { groupChatService } from "@/lib/groupChatService";
import {
  chatService,
  deepResearchService,
  historyService,
  microAppService,
  tagService,
  taskService,
} from "@/lib/services";
import type { HistoryItem, HistoryItemType, TagSummary } from "@/types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type TypeTab = "all" | HistoryItemType;
type StatusFilter = "shared" | "shared_with_me";

const TYPE_TABS_ALL: { value: TypeTab; label: string; featureFlag?: keyof typeof featureFlags }[] = [
  { value: "all", label: "All" },
  { value: "chat", label: "Chat" },
  { value: "brainstorm", label: "Brainstorm", featureFlag: "brainstorm" },
  { value: "canvas", label: "Canvas", featureFlag: "canvas" },
  { value: "task", label: "Autopilot", featureFlag: "autopilot" },
  { value: "deep_research", label: "Deep Research" },
  { value: "micro_app", label: "Micro Apps", featureFlag: "microApps" },
];

const TYPE_TABS = TYPE_TABS_ALL.filter((t) => !t.featureFlag || featureFlags[t.featureFlag]);

const STATUS_CHIPS: { value: StatusFilter; label: string; icon: typeof Star }[] = [
  { value: "shared", label: "Shared", icon: Share2 },
  { value: "shared_with_me", label: "Shared with Me", icon: Users },
];

const TYPE_META: Record<HistoryItemType, { icon: typeof MessageSquare; label: string; color: string; route: string }> =
  {
    chat: {
      icon: MessageSquare,
      label: "Chat",
      color: "text-blue-400",
      route: "/chat",
    },
    brainstorm: {
      icon: MessagesSquare,
      label: "Brainstorm",
      color: "text-violet-400",
      route: "/group-chat",
    },
    task: {
      icon: ListTodo,
      label: "Autopilot",
      color: "text-amber-400",
      route: "/tasks",
    },
    deep_research: {
      icon: Telescope,
      label: "Deep Research",
      color: "text-rose-400",
      route: "/deep-research",
    },
    canvas: {
      icon: PaintbrushVertical,
      label: "Canvas",
      color: "text-emerald-400",
      route: "/canvas",
    },
    micro_app: {
      icon: LayoutDashboard,
      label: "Micro App",
      color: "text-orange-400",
      route: "/apps/view",
    },
  };

const GROUP_ORDER = ["Today", "Yesterday", "Last 7 Days", "Older"] as const;

/** Maps each history item type to its human-readable label and delete service call. */
const DELETE_META: Record<HistoryItemType, { label: string; deleteFn: (uuid: string) => Promise<unknown> }> = {
  chat: { label: "Chat", deleteFn: (uuid) => chatService.deleteSession(uuid) },
  brainstorm: { label: "Brainstorm", deleteFn: (uuid) => groupChatService.deleteSession(uuid) },
  task: { label: "Autopilot Task", deleteFn: (uuid) => taskService.delete(uuid) },
  deep_research: { label: "Deep Research", deleteFn: (uuid) => deepResearchService.delete(uuid) },
  canvas: { label: "Canvas", deleteFn: (uuid) => chatService.deleteSession(uuid) },
  // Micro apps only appear via "shared with me" (delete button is hidden for
  // those); this entry exists to satisfy the map and delegates to the
  // admin-gated delete endpoint if ever reached.
  micro_app: { label: "Micro App", deleteFn: (uuid) => microAppService.delete(uuid) },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeGroup(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(now);
  lastWeek.setDate(lastWeek.getDate() - 7);

  if (date.toDateString() === now.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  if (date >= lastWeek) return "Last 7 Days";
  return "Older";
}

function navigateToItem(item: HistoryItem, navigate: ReturnType<typeof useNavigate>) {
  const meta = TYPE_META[item.type];
  navigate(`${meta.route}/${item.uuid}`);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function History() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [items, setItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<TypeTab>("all");
  const [activeStatuses, setActiveStatuses] = useState<Set<StatusFilter>>(new Set());
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [availableTags, setAvailableTags] = useState<TagSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const PAGE_SIZE = 50;

  // Load available tags once
  useEffect(() => {
    tagService
      .list({ pageSize: 100 })
      .then((res) => {
        setAvailableTags(res.data);
      })
      .catch((err) => {
        console.error("Failed to load tags:", err);
      });
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: Parameters<typeof historyService.list>[0] = {
        page,
        pageSize: PAGE_SIZE,
        sort: "recent",
      };

      if (activeTab !== "all") {
        params.types = activeTab;
      }

      if (activeStatuses.size > 0) {
        params.status = Array.from(activeStatuses).join(",");
      }

      if (activeTags.size > 0) {
        params.tags = Array.from(activeTags).join(",");
      }

      const res = await historyService.list(params);
      setItems(res.data);
      setTotal(res.meta.total);
      setTotalPages(res.meta.totalPages);
    } catch (err) {
      console.error("Failed to load history:", err);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, activeStatuses, activeTags, page]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [activeTab, activeStatuses, activeTags]);

  const toggleStatus = (status: StatusFilter) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const toggleTag = (tagUuid: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagUuid)) {
        next.delete(tagUuid);
      } else {
        next.add(tagUuid);
      }
      return next;
    });
  };

  const handleDelete = async (item: HistoryItem) => {
    const meta = DELETE_META[item.type];

    const confirmed = await confirm({
      title: `Delete ${meta.label}`,
      message: `Are you sure you want to delete "${item.title || "Untitled"}"? This action cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });

    if (!confirmed) return;

    try {
      await meta.deleteFn(item.uuid);
      setItems((prev) => prev.filter((i) => i.uuid !== item.uuid));
      setTotal((prev) => Math.max(0, prev - 1));
      showToast(`${meta.label} deleted`, "success");
    } catch (err) {
      console.error(`Failed to delete ${meta.label}:`, err);
      showToast(`Failed to delete ${meta.label.toLowerCase()}`, "error");
    }
  };

  // Client-side search filter (search is lightweight enough to stay client-side)
  const filtered = searchQuery
    ? items.filter(
        (item) =>
          (item.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.preview || "").toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : items;

  // Group by time
  const grouped = filtered.reduce<Record<string, HistoryItem[]>>((acc, item) => {
    const group = timeGroup(item.updatedAt);
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isLoading && items.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader icon={HistoryIcon} title="History" subtitle="Browse all your chats, brainstorms, tasks, and research">
        {/* Type tabs */}
        <div className="mt-4 flex items-center gap-1 overflow-x-auto">
          {TYPE_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                activeTab === tab.value
                  ? "bg-brand-primary-subtle text-brand-primary border border-brand-primary/30"
                  : "text-content-tertiary hover:bg-interactive-hover border border-transparent"
              }`}
            >
              {tab.value !== "all" &&
                (() => {
                  const Icon = TYPE_META[tab.value].icon;
                  return <Icon className="w-3.5 h-3.5" />;
                })()}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search + status chips */}
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-quaternary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search history..."
              className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
          <div className="flex items-center gap-2">
            {STATUS_CHIPS.map((chip) => {
              const Icon = chip.icon;
              const isActive = activeStatuses.has(chip.value);
              return (
                <button
                  key={chip.value}
                  onClick={() => toggleStatus(chip.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    isActive
                      ? "bg-brand-primary-subtle text-brand-primary border border-brand-primary/30"
                      : "bg-surface-primary text-content-tertiary border border-border-primary hover:bg-interactive-hover"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tag filters */}
        {availableTags.length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Tag className="w-4 h-4 text-content-quaternary shrink-0" />
            {availableTags.map((tag) => {
              const isActive = activeTags.has(tag.uuid);
              return (
                <button
                  key={tag.uuid}
                  onClick={() => toggleTag(tag.uuid)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition border ${
                    isActive ? "" : "border-border-primary text-content-tertiary hover:bg-interactive-hover"
                  }`}
                  style={
                    isActive
                      ? {
                          backgroundColor: `${tag.color}20`,
                          borderColor: `${tag.color}50`,
                          color: tag.color,
                        }
                      : undefined
                  }
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                  {tag.name}
                  {isActive && <X className="w-3 h-3 ml-0.5" />}
                </button>
              );
            })}
            {activeTags.size > 0 && (
              <button
                onClick={() => setActiveTags(new Set())}
                className="text-xs text-content-quaternary hover:text-content-tertiary transition ml-1"
              >
                Clear tags
              </button>
            )}
          </div>
        )}
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <MessageSquare className="w-16 h-16 text-content-quaternary mb-4" />
            <h3 className="text-xl font-semibold text-content-primary mb-2">Nothing here yet</h3>
            <p className="text-content-tertiary">
              {(() => {
                if (searchQuery) return "Try a different search term";
                if (activeStatuses.size > 0 || activeTags.size > 0) return "No items match the selected filters";
                return "Start a new chat, brainstorm, or task to see it here";
              })()}
            </p>
          </div>
        )}

        {!isLoading && filtered.length > 0 && (
          <div className="space-y-8">
            {GROUP_ORDER.map((group) => {
              const groupItems = grouped[group];
              if (!groupItems || groupItems.length === 0) return null;

              return (
                <div key={group}>
                  <div className="flex items-center gap-3 mb-4">
                    <Calendar className="w-5 h-5 text-content-tertiary" />
                    <h2 className="text-lg font-semibold text-content-primary">{group}</h2>
                    <div className="flex-1 h-px bg-surface-secondary" />
                    <span className="text-xs text-content-quaternary">
                      {groupItems.length} item{groupItems.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupItems.map((item) => (
                      <HistoryCard
                        key={`${item.type}-${item.uuid}`}
                        item={item}
                        onNavigate={() => navigateToItem(item, navigate)}
                        onOpenNewTab={() => {
                          const meta = TYPE_META[item.type];
                          window.open(`${meta.route}/${item.uuid}`, "_blank");
                        }}
                        onDelete={() => handleDelete(item)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-border-primary">
            <span className="text-sm text-content-tertiary">
              Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-border-primary text-content-tertiary hover:bg-interactive-hover disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </button>
              <span className="text-sm text-content-secondary px-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-border-primary text-content-tertiary hover:bg-interactive-hover disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HistoryCard sub-component
// ---------------------------------------------------------------------------

function HistoryCard({
  item,
  onNavigate,
  onOpenNewTab,
  onDelete,
}: Readonly<{
  item: HistoryItem;
  onNavigate: () => void;
  onOpenNewTab: () => void;
  onDelete: () => void;
}>) {
  const meta = TYPE_META[item.type];
  const TypeIcon = meta.icon;

  const borderClass = item.isSharedWithMe
    ? "border-brand-secondary/20 hover:border-brand-secondary/40"
    : "border-border-primary hover:border-border-secondary";

  return (
    <div
      role="button" // NOSONAR — contains nested interactive elements
      tabIndex={0}
      onClick={onNavigate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onNavigate();
        }
      }}
      className={`bg-surface-primary border ${borderClass} rounded-xl p-5 transition group cursor-pointer`}
    >
      {/* Top row: type badge + actions */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-content-primary font-semibold truncate">{item.title || "Untitled"}</h3>
          </div>
          <p className="text-xs text-content-quaternary">{new Date(item.updatedAt).toLocaleString()}</p>
        </div>

        {/* Hover actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition shrink-0 ml-2">
          {item.isStarred && <Star className="w-4 h-4 text-amber-400 fill-amber-400" />}
          {!item.isSharedWithMe && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1.5 hover:bg-red-500/10 rounded-lg transition text-content-tertiary hover:text-red-400"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onOpenNewTab();
            }}
            className="p-1.5 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Preview text */}
      {item.preview && <p className="text-sm text-content-tertiary line-clamp-2 mb-3">{item.preview}</p>}

      {/* Tags */}
      {item.tags.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <Tag className="w-3 h-3 text-content-quaternary shrink-0" />
          {item.tags.slice(0, 3).map((tag) => (
            <span
              key={tag.uuid}
              className="text-xs px-1.5 py-0.5 rounded-full border border-border-primary text-content-tertiary"
              style={{ borderColor: `${tag.color}40`, color: tag.color }}
            >
              {tag.name}
            </span>
          ))}
          {item.tags.length > 3 && <span className="text-xs text-content-quaternary">+{item.tags.length - 3}</span>}
        </div>
      )}

      {/* Bottom row: type, message count, status badges */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`flex items-center gap-1 text-xs px-2 py-0.5 bg-surface-secondary rounded-full ${meta.color}`}
          >
            <TypeIcon className="w-3 h-3" />
            {meta.label}
          </span>

          {item.messageCount != null && item.messageCount > 0 && (
            <span className="text-xs text-content-quaternary">
              {item.messageCount} msg{item.messageCount === 1 ? "" : "s"}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {item.isStarred && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 group-hover:hidden" />}
          {item.isShared && (
            <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 bg-brand-primary-subtle text-brand-primary rounded-full">
              <Share2 className="w-3 h-3" />
              Shared
            </span>
          )}
          {item.isSharedWithMe && (
            <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 bg-brand-secondary-subtle text-brand-secondary rounded-full">
              <Users className="w-3 h-3" />
              Shared
            </span>
          )}
          {item.status && item.type === "task" && <TaskStatusBadge status={item.status} />}
          {item.status && item.type === "deep_research" && <ResearchStatusBadge status={item.status} />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status badges
// ---------------------------------------------------------------------------

function TaskStatusBadge({ status }: Readonly<{ status: string }>) {
  const styles: Record<string, string> = {
    pending: "bg-yellow-500/10 text-yellow-400",
    in_progress: "bg-blue-500/10 text-blue-400",
    running: "bg-blue-500/10 text-blue-400",
    completed: "bg-green-500/10 text-green-400",
    failed: "bg-red-500/10 text-red-400",
    cancelled: "bg-slate-500/10 text-slate-400",
  };
  const label = status.replace(/_/g, " ");
  return (
    <span
      className={`text-xs px-1.5 py-0.5 rounded-full capitalize ${styles[status] || "bg-surface-secondary text-content-quaternary"}`}
    >
      {label}
    </span>
  );
}

function ResearchStatusBadge({ status }: Readonly<{ status: string }>) {
  const styles: Record<string, string> = {
    pending: "bg-yellow-500/10 text-yellow-400",
    in_progress: "bg-blue-500/10 text-blue-400",
    researching: "bg-blue-500/10 text-blue-400",
    completed: "bg-green-500/10 text-green-400",
    failed: "bg-red-500/10 text-red-400",
  };
  const label = status.replace(/_/g, " ");
  return (
    <span
      className={`text-xs px-1.5 py-0.5 rounded-full capitalize ${styles[status] || "bg-surface-secondary text-content-quaternary"}`}
    >
      {label}
    </span>
  );
}
